import React from 'react';
import { Link } from 'react-router-dom';
import { ValidatorHealthSummary, ValidatorHealthStatus } from '../../types/api';

interface ValidatorsStatusProps {
  healthSummary: ValidatorHealthSummary | null;
}

const ValidatorsStatus: React.FC<ValidatorsStatusProps> = ({ healthSummary }) => {
  if (!healthSummary || !healthSummary.validatorStatuses) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Validators Status</h3>
            <Link
              to="/validators"
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              View details →
            </Link>
          </div>
        </div>
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading validators status...</p>
        </div>
      </div>
    );
  }

  const validators = healthSummary.validatorStatuses;
  const offlineValidators = validators.filter(v => !v.isHealthy);
  
  // Use health monitoring averages from the summary
  const averageResponseTime = healthSummary.averageResponseTime;
  
  // Calculate average success rate based on health status (simplified metric)
  const averageSuccessRate = validators.length > 0 
    ? (healthSummary.healthyValidators / healthSummary.totalValidators) * 100
    : 0;

  const getResponseTimeColor = (responseTime: number) => {
    if (responseTime < 1000) return 'text-green-600';
    if (responseTime < 3000) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getStatusDisplay = (validator: ValidatorHealthStatus) => {
    if (validator.isHealthy) {
      return { color: 'text-green-600', text: 'Healthy' };
    } else if (validator.consecutiveFailures >= 5) {
      return { color: 'text-red-600', text: 'Critical' };
    } else {
      return { color: 'text-yellow-600', text: 'Unhealthy' };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Validators Status</h3>
          <Link
            to="/validators"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            View details →
          </Link>
        </div>
      </div>
      
      {/* Overview Stats */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{healthSummary.totalValidators}</p>
            <p className="text-sm text-gray-500">Total</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-green-600">{healthSummary.healthyValidators}</p>
            <p className="text-sm text-gray-500">Healthy</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-semibold ${getSuccessRateColor(averageSuccessRate)}`}>
              {averageSuccessRate.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-500">Health Rate</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-semibold ${getResponseTimeColor(averageResponseTime)}`}>
              {formatResponseTime(averageResponseTime)}
            </p>
            <p className="text-sm text-gray-500">Avg Response</p>
          </div>
        </div>
      </div>
      
      {/* Validators List */}
      <div className="divide-y divide-gray-200">
        {validators.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <div className="text-gray-400 mb-2">
              <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-sm">No validators configured</p>
          </div>
        ) : (
          validators.slice(0, 5).map((validator) => {
            const statusDisplay = getStatusDisplay(validator);
            return (
              <div key={validator.validator} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      validator.isHealthy ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {validator.name || `Validator ${validator.validator.slice(0, 8)}...`}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">
                        {validator.validator}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="text-center">
                        <p className={`font-semibold ${statusDisplay.color}`}>
                          {statusDisplay.text}
                        </p>
                        <p className="text-xs text-gray-500">Status</p>
                      </div>
                      <div className="text-center">
                        <p className={`font-semibold ${getResponseTimeColor(validator.responseTime)}`}>
                          {formatResponseTime(validator.responseTime)}
                        </p>
                        <p className="text-xs text-gray-500">Response</p>
                      </div>
                      {validator.consecutiveFailures > 0 && (
                        <div className="text-center">
                          <p className="font-semibold text-red-600">
                            {validator.consecutiveFailures}
                          </p>
                          <p className="text-xs text-gray-500">Failures</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Last checked: {getTimeAgo(validator.lastChecked)}
                    </p>
                    {validator.errorMessage && (
                      <p className="text-xs text-red-500 mt-1 truncate max-w-xs">
                        {validator.errorMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Show offline validators warning */}
      {offlineValidators.length > 0 && (
        <div className="px-6 py-3 bg-red-50 border-t border-red-200">
          <div className="flex items-center space-x-2">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-800">
              <span className="font-medium">{offlineValidators.length}</span> validator(s) are currently offline
            </p>
          </div>
        </div>
      )}
      
      {validators.length > 5 && (
        <div className="px-6 py-3 bg-gray-50 text-center">
          <Link
            to="/validators"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            View all {validators.length} validators
          </Link>
        </div>
      )}
    </div>
  );
};

export default ValidatorsStatus; 